import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Status } from 'src/models/AuthContextModels';
import type { RootState } from 'src/store/store';
import { isActionOutstanding, isSuppressed, XptNotification } from './notificationModels';
import { getMyNotifications, markMyNotificationsRead } from './notificationService';

interface NotificationsState {
  items: XptNotification[]; // newest first, merged by notification_id
  status: Status;
  // Snapshot of item ids when a baseline fetch starts. Used by `fulfilled` to tell a stale/deleted
  // item (present at fetch start, absent from the response => drop) apart from a live push that
  // arrived mid-fetch (absent at fetch start => keep, even if the response predates it).
  idsAtFetchStart: string[];
}

const initialState: NotificationsState = {
  items: [],
  status: Status.NotInitiated,
  idsAtFetchStart: []
};

/** Baseline load (initial + on reconnect/refocus).
 * (`condition` skips the dispatch if a fetch is already in-flight) */
export const fetchMyNotifications = createAsyncThunk('notifications/fetch', async () => await getMyNotifications(), {
  condition: (_arg, { getState }) => (getState() as RootState).notificationsStore.status !== Status.Loading
});

/** Advance the read watermark */
export const markNotificationsReadThunk = createAsyncThunk('notifications/markRead', async () => {
  await markMyNotificationsRead();
});

/** Merge one notification by id: unseen id => prepend (new); known id => update in place (modification) */
const mergeById = (items: XptNotification[], incoming: XptNotification): XptNotification[] => {
  const idx = items.findIndex((n) => n.notification_id === incoming.notification_id);
  if (idx === -1) {
    return [incoming, ...items];
  }
  const next = items.slice();
  next[idx] = { ...next[idx], ...incoming };
  return next;
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // A pushed notification from the subscription (new or in-place update).
    notificationReceived: (state, action: PayloadAction<XptNotification>) => {
      // A cancellation is never surfaced (see isSuppressed). It can arrive as a brand-new item
      // or as an in-place update to a pending item already in the list, so drop the incoming
      // item AND remove whatever it was resolving — otherwise the reviewer would keep a
      // "needs review" row for a request that no longer exists.
      if (isSuppressed(action.payload)) {
        state.items = state.items.filter((n) => n.notification_id !== action.payload.notification_id);
        return;
      }
      state.items = mergeById(state.items, action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyNotifications.pending, (state) => {
        state.status = Status.Loading;
        state.idsAtFetchStart = state.items.map((n) => n.notification_id);
      })
      .addCase(fetchMyNotifications.fulfilled, (state, action: PayloadAction<XptNotification[]>) => {
        // Take the SERVER's ordering (ORDER BY updated_at DESC) as the source of truth — the client
        // has no timestamp to sort on, so we must honor the response order rather than the order items
        // happened to arrive in. Live pushes that landed AFTER this fetch started (ids absent from
        // both the response and the fetch-start snapshot) are kept on top, newest-first.
        // serverIds covers the UNFILTERED response so a suppressed item the server still returns
        // is recognized as known and never mistaken for a mid-fetch push.
        const serverIds = new Set(action.payload.map((n) => n.notification_id));
        const idsAtFetchStart = new Set(state.idsAtFetchStart);
        const midFetchPushes = state.items.filter((n) => !serverIds.has(n.notification_id) && !idsAtFetchStart.has(n.notification_id));
        state.items = [...midFetchPushes, ...action.payload.filter((n) => !isSuppressed(n))];
        state.idsAtFetchStart = [];
        state.status = Status.Completed;
      })
      .addCase(fetchMyNotifications.rejected, (state) => {
        state.status = Status.Failed;
      })
      .addCase(markNotificationsReadThunk.fulfilled, (state) => {
        // Watermark advanced server-side: optimistically grey everything except items that
        // still represent an outstanding action, which the watermark cannot clear.
        state.items = state.items.map((n) => (isActionOutstanding(n) ? n : { ...n, is_read: true }));
      });
  }
});

export const { notificationReceived } = notificationsSlice.actions;
export default notificationsSlice.reducer;
